﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import type {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  OrganizerSessionResponse,
  ResendVerificationRequest,
  ResetPasswordRequest,
  SignupRequest,
  SignupResponse,
  UpdateOrganizerLegalInfoRequest,
  UpdateOrganizerLogoRequest,
  VerifyEmailRequest,
} from "@ticketnjoy/types";

import { ApiError, apiClient } from "@/lib/api-client";

export const meQueryKey = ["organizer", "me"] as const;

/**
 * GET /organizers/me → mevcut organizer + shops.
 * 401 durumunda hata ApiError olarak fırlar, retry yapılmaz (auth yok anlamına gelir).
 */
export function useMeQuery() {
  return useQuery<OrganizerSessionResponse, ApiError>({
    queryKey: meQueryKey,
    queryFn: () => apiClient<OrganizerSessionResponse>("/organizers/me"),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 2;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * `/me` 401 dönerse login sayfasına yönlendiren hook.
 * Dashboard layout'unda kullanılır.
 */
export function useAuthenticatedMe() {
  const navigate = useNavigate();
  const query = useMeQuery();

  useEffect(() => {
    if (query.error instanceof ApiError && query.error.status === 401) {
      navigate("/login", { replace: true });
    }
  }, [query.error, navigate]);

  return query;
}

export function useSignupMutation() {
  return useMutation<SignupResponse, Error, SignupRequest>({
    mutationFn: (body) =>
      apiClient<SignupResponse>("/organizers/signup", {
        method: "POST",
        body,
      }),
  });
}

export function useLoginMutation() {
  const qc = useQueryClient();
  return useMutation<OrganizerSessionResponse, Error, LoginRequest>({
    mutationFn: (body) =>
      apiClient<OrganizerSessionResponse>("/organizers/login", {
        method: "POST",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(meQueryKey, data);
    },
  });
}

export function useVerifyEmailMutation() {
  const qc = useQueryClient();
  return useMutation<OrganizerSessionResponse, Error, VerifyEmailRequest>({
    mutationFn: (body) =>
      apiClient<OrganizerSessionResponse>("/organizers/verify-email", {
        method: "POST",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(meQueryKey, data);
    },
  });
}

export function useResendVerificationMutation() {
  return useMutation<void, Error, ResendVerificationRequest>({
    mutationFn: (body) =>
      apiClient<void>("/organizers/resend-verification", {
        method: "POST",
        body,
      }),
  });
}

/** Email kayıtlı olsun olmasın 204 döner — UI her durumda aynı mesajı gösterir. */
export function useForgotPasswordMutation() {
  return useMutation<void, Error, ForgotPasswordRequest>({
    mutationFn: (body) =>
      apiClient<void>("/organizers/forgot-password", {
        method: "POST",
        body,
      }),
  });
}

/** Başarılıysa tüm session'lar kapanır; kullanıcı login ekranına yönlendirilir. */
export function useResetPasswordMutation() {
  return useMutation<void, Error, ResetPasswordRequest>({
    mutationFn: (body) =>
      apiClient<void>("/organizers/reset-password", {
        method: "POST",
        body,
      }),
  });
}

/** Mevcut session açık kalır, diğer cihazlardaki session'lar kapanır. */
export function useChangePasswordMutation() {
  return useMutation<void, Error, ChangePasswordRequest>({
    mutationFn: (body) =>
      apiClient<void>("/organizers/me/password", {
        method: "POST",
        body,
      }),
  });
}

/**
 * PATCH /organizers/me/logo — sadece logo alanını günceller.
 * null gönderirse logo kaldırılır.
 */
export function useUpdateLogoMutation() {
  const qc = useQueryClient();
  return useMutation<OrganizerSessionResponse, ApiError, UpdateOrganizerLogoRequest>({
    mutationFn: (body) =>
      apiClient<OrganizerSessionResponse>("/organizers/me/logo", {
        method: "PATCH",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(meQueryKey, data);
    },
  });
}

/**
 * PATCH /organizers/me/legal-info — yasal bilgi formunu kaydeder.
 * Tüm alanlar zorunlu; backend format validasyonu yapar.
 */
export function useUpdateLegalInfoMutation() {
  const qc = useQueryClient();
  return useMutation<OrganizerSessionResponse, ApiError, UpdateOrganizerLegalInfoRequest>({
    mutationFn: (body) =>
      apiClient<OrganizerSessionResponse>("/organizers/me/legal-info", {
        method: "PATCH",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(meQueryKey, data);
    },
  });
}

export function useLogoutMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => apiClient<void>("/organizers/logout", { method: "POST" }),
    onSuccess: () => {
      qc.clear();
    },
  });
}
